import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional custom fallback; defaults to a friendly "something went wrong" panel. */
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * #263 — defense-in-depth route error boundary.
 *
 * Before this existed, a single component throwing during render (e.g. EditorPage
 * calling the data-router-only `useBlocker` under <BrowserRouter>) unmounted the
 * entire React tree and left users staring at a blank page with no recovery path.
 *
 * This boundary catches any render error in its subtree, reports it to Sentry, and
 * renders a friendly recovery panel so a single broken component can never blank the
 * whole app again.
 */
export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // #988: dynamic import so App→ErrorBoundary does not pull @sentry/react
    // into the homepage initial graph.
    void import('../utils/sentry')
      .then(({ captureException }) => {
        captureException(error, {
          source: 'ErrorBoundary',
          componentStack: info.componentStack ?? undefined,
        });
      })
      .catch(() => {
        // Reporting failed — recovery UI still renders.
      });
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div
        role="alert"
        className="min-h-screen flex items-center justify-center bg-slate-900 px-4"
      >
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 max-w-md w-full text-center shadow-2xl">
          <h1 className="text-xl font-semibold text-white mb-2">Something went wrong</h1>
          <p className="text-slate-400 text-sm mb-6">
            This page hit an unexpected error. Your saved designs are safe — reloading
            usually fixes it.
          </p>
          <button
            onClick={this.handleReload}
            className="btn bg-orange-500 hover:bg-orange-400 text-white px-6 py-2 text-sm rounded-lg transition-colors"
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
